import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { LayoutGrid, Users, Briefcase, Crown, FileText, BookOpen, LogOut, Globe, CalendarClock, Heart } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth, hasRole } from "../context/AuthContext";
import { ReminderWatcher } from "./desk/ReminderWatcher";

const NAV = [
  { to: "/app/desk", label: "Partners' Desk", icon: CalendarClock, roles: ["partner", "secretary"] },
  { to: "/app/circle", label: "Private Circle", icon: Heart, roles: ["partner", "secretary", "circle"] },
  { to: "/app/exec", label: "Executive Intelligence", icon: Crown, roles: ["executive", "gm", "partner"] },
  { to: "/app/narrative", label: "Presentation", icon: BookOpen, roles: ["executive", "gm", "partner"] },
  { to: "/app/report", label: "Daily Report", icon: FileText, roles: ["executive", "gm", "partner"] },
  { to: "/app/ops", label: "Operations Hub", icon: LayoutGrid, roles: ["executive", "gm", "hr", "manager", "staff", "partner"] },
  { to: "/app/hr", label: "Talent & HR", icon: Briefcase, roles: ["hr", "gm"] },
  { to: "/app/users", label: "Accounts", icon: Users, roles: ["gm"] },
];

export const RequireRole = ({ roles, children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="p-10 font-data text-xs text-slate-500">Verifying access…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !hasRole(user, roles)) return <div className="p-10 text-slate-400" data-testid="access-denied">You do not have access to this area.</div>;
  return children;
};

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = NAV.filter((n) => hasRole(user, n.roles));
  return (
    <RequireRole>
      <div className="min-h-screen lg:flex">
        <aside className="no-print lg:w-64 lg:min-h-screen lg:sticky lg:top-0 bg-[#F4F8F5] border-b lg:border-b-0 lg:border-r border-[color:var(--line)] flex lg:flex-col">
          <div className="p-4 lg:p-6 hidden lg:block"><Logo size={44} /></div>
          <nav className="flex lg:flex-col overflow-x-auto lg:overflow-visible gap-1 p-2 lg:px-3 flex-1">
            {items.map(({ to, label, icon: I }) => (
              <NavLink key={to} to={to} data-testid={`side-nav-${to.split("/").pop()}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm whitespace-nowrap rounded-sm transition-colors ${isActive ? "bg-[#0B6B3A] text-white border-l-2 border-[color:var(--gold-hi)]" : "text-slate-400 hover:text-ink"}`}>
                <I size={16} /> {label}
              </NavLink>
            ))}
            <NavLink to="/" className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-400 hover:text-ink whitespace-nowrap" data-testid="side-nav-public"><Globe size={16} /> Public Hub</NavLink>
          </nav>
          <div className="hidden lg:block p-4 border-t border-[color:var(--line)]">
            <div className="text-sm text-ink" data-testid="current-user-name">{user?.name}</div>
            <div className="font-data text-[10px] uppercase tracking-widest text-gold" data-testid="current-user-role">{user?.role}</div>
            <button onClick={() => { logout(); nav("/login"); }} className="mt-3 flex items-center gap-2 text-xs text-slate-400 hover:text-red-400" data-testid="logout-btn"><LogOut size={14} /> Sign out</button>
          </div>
          <button onClick={() => { logout(); nav("/login"); }} className="lg:hidden px-3 text-slate-400" data-testid="logout-btn-mobile"><LogOut size={16} /></button>
        </aside>
        <main className="flex-1 min-w-0"><Outlet /></main>
        {hasRole(user, ["partner", "secretary"]) && <ReminderWatcher />}
      </div>
    </RequireRole>
  );
}
